import React from 'react';
import { clickActions, NodeProps, treeTypes } from './types';
import {
	composeHandlers,
	getAccessibleRange,
	getAriaSelected,
	getDescendants,
	isBranchNode,
	propagatedIds,
} from './utils';
import { NodeGroup } from './viewNodeGroup';

export const Node = (props: NodeProps) => {
	const {
		element,
		dispatch,
		data,
		selectedIds,
		tabbableId,
		isFocused,
		expandedIds,
		disabledIds,
		halfSelectedIds,
		lastUserSelect,
		nodeRefs,
		baseClassNames,
		nodeRenderer,
		setsize,
		posinset,
		level,
		propagateCollapse,
		propagateSelect,
		multiSelect,
		togglableSelect,
		clickAction,
		state,
		size,
	} = props;

	const handleExpand = (event: any) => {
		if (event.ctrlKey || event.altKey || event.shiftKey) return;
		if (expandedIds.has(element.id) && propagateCollapse) {
			const ids = [element.id, ...getDescendants(data, element.id, new Set())];
			dispatch({
				type: treeTypes.collapseMany,
				ids,
				lastInteractedWith: element.id,
			});
		} else {
			dispatch({
				type: treeTypes.toggle,
				id: element.id,
				lastInteractedWith: element.id,
			});
		}
	};

	const handleFocus = () =>
		dispatch({
			type: treeTypes.focus,
			id: element.id,
			lastInteractedWith: element.id,
		});

	const handleSelect = (event: any) => {
		if (event.shiftKey) {
			let ids = getAccessibleRange({
				data,
				expandedIds,
				from: lastUserSelect,
				to: element.id,
				lastInteractedWith: element.id,
			}).filter((id: any) => !disabledIds.has(id));
			ids = propagateSelect ? propagatedIds(data, ids, disabledIds) : ids;
			dispatch({
				type: treeTypes.exclusiveChangeSelectMany,
				select: true,
				multiSelect,
				ids,
				lastInteractedWith: element.id,
			});
		} else if (event.ctrlKey || clickAction === clickActions.select) {
			//Select
			dispatch({
				type: togglableSelect ? treeTypes.toggleSelect : treeTypes.select,
				id: element.id,
				multiSelect,
				lastInteractedWith: element.id,
			});
			propagateSelect &&
				!disabledIds.has(element.id) &&
				dispatch({
					type: treeTypes.changeSelectMany,
					ids: propagatedIds(data, [element.id], disabledIds),
					select: togglableSelect ? !selectedIds.has(element.id) : true,
					multiSelect,
					lastInteractedWith: element.id,
				});
		} else if (clickAction === clickActions.exclusiveSelect) {
			dispatch({
				type: togglableSelect ? treeTypes.toggleSelect : treeTypes.select,
				id: element.id,
				multiSelect: false,
				lastInteractedWith: element.id,
			});
		} else if (clickAction === clickActions.focus) {
			dispatch({
				type: treeTypes.focus,
				id: element.id,
				lastInteractedWith: element.id,
			});
		}
	};

	const getClasses = (className: any) => {
		let _return = '';
		if (expandedIds.has(element.id)) {
			_return = _return + ` ${className}--expanded`;
		}
		if (selectedIds.has(element.id)) {
			_return = _return + ` ${className}--selected`;
		}
		if (tabbableId === element.id && isFocused) {
			_return = _return + ` ${className}--focused`;
		}
		if (tabbableId === element.id) {
			_return = _return + ` ${className}--last`;
		}

		return _return;
	};

	const getLeafProps = ({ onClick }: any = {}) => {
		return {
			role: 'treeitem',
			tabIndex: tabbableId === element.id ? 0 : -1,
			onClick:
				onClick == null ? composeHandlers(handleSelect, handleFocus) : composeHandlers(onClick, handleFocus),
			ref: (x: any) => (nodeRefs.current[element.id] = x),
			className: getClasses(baseClassNames.node) + ' ' + baseClassNames.leaf,
			'aria-setsize': setsize,
			'aria-posinset': posinset,
			'aria-level': level,
			'aria-selected': getAriaSelected({
				isSelected: selectedIds.has(element.id),
				isDisabled: disabledIds.has(element.id),
				multiSelect,
			}),
			size: size,
			disabled: disabledIds.has(element.id),
			'aria-disabled': disabledIds.has(element.id),
			...{ 'data-elementid': element.id, 'data-elementname': element.name },
		};
	};

	const getBranchProps = ({ onClick }: any = {}) => {
		return {
			onClick:
				onClick == null
					? composeHandlers(handleSelect, handleExpand, handleFocus)
					: composeHandlers(onClick, handleFocus),
			className: getClasses(baseClassNames.node) + ' ' + baseClassNames.branch,
			'data-selected': getAriaSelected({
				isSelected: selectedIds.has(element.id),
				isDisabled: disabledIds.has(element.id),
				multiSelect,
			}),
			size: size,
			disabled: disabledIds.has(element.id),
			'data-disabled': disabledIds.has(element.id),
			...{ 'data-elementid': element.id, 'data-elementname': element.name },
		};
	};

	return isBranchNode(data, element.id) ? (
		<li
			style={{ listStyle: 'none', backgroundColor: 'var(--epods--color-bg)' }}
			role="treeitem"
			aria-expanded={expandedIds.has(element.id)}
			aria-selected={getAriaSelected({
				isSelected: selectedIds.has(element.id),
				isDisabled: disabledIds.has(element.id),
				multiSelect,
			})}
			aria-setsize={setsize}
			aria-posinset={posinset}
			aria-level={level}
			{...{ disabled: disabledIds.has(element.id) }}
			aria-disabled={disabledIds.has(element.id)}
			tabIndex={tabbableId === element.id ? 0 : -1}
			ref={(x: any) => (nodeRefs.current[element.id] = x)}
			className={baseClassNames.branchWrapper}
		>
			{nodeRenderer({
				element,
				isBranch: true,
				isSelected: selectedIds.has(element.id),
				isHalfSelected: halfSelectedIds.has(element.id),
				isExpanded: expandedIds.has(element.id),
				isDisabled: disabledIds.has(element.id),
				dispatch,
				getNodeProps: getBranchProps,
				setsize,
				posinset,
				level,
				handleSelect,
				handleExpand,
				treeState: state,
			})}
			<NodeGroup getClasses={getClasses} {...props} /> {/* //element={element} */}
		</li>
	) : (
		<li
			style={{ listStyle: 'none', backgroundColor: 'var(--epods--color-bg)' }}
			role="none"
			className={getClasses(baseClassNames.leafListItem) + ''}
		>
			{nodeRenderer({
				element,
				isBranch: false,
				isSelected: selectedIds.has(element.id),
				isHalfSelected: undefined,
				isExpanded: false,
				isDisabled: disabledIds.has(element.id),
				dispatch,
				getNodeProps: getLeafProps,
				setsize,
				posinset,
				level,
				handleSelect,
				handleExpand: () => null,
				treeState: state,
			})}
		</li>
	);
};
